import React from 'react';

import { AgenticCheckoutValue, ConfigResponse } from '../../store/modules/merchant/types';
import PageWrapper from '../../common/PageWrapper';
import { useMerchant } from '../../contexts/MerchantContext';
import { AgenticCheckoutForm } from './components/AgenticCheckoutForm';
import { toast } from '../../util/toast';
import {
  useGetCallbacksQuery,
  useGetConfigsQuery,
  useDeleteCallbackMutation,
} from '../../store/modules/merchant/api';
import { useConfigUpsert } from '../../hooks';
import { useCallbackUpsert } from '../../hooks/useCallbackUpsert';
export const AgenticCheckout = () => {
  const { merchantId, isLoading: merchantInfoLoading, canUpdateMerchantSettings } = useMerchant();
  const hasModifyAccess = canUpdateMerchantSettings();
  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });

  const { data: callbacks, isLoading: isLoadingCallbacks } = useGetCallbacksQuery(merchantId, {
    skip: !merchantId,
  });

  const agenticCheckoutConfig = configs?.find((c) => c.name === 'AGENTIC_CHECKOUT') as
    ConfigResponse<AgenticCheckoutValue> | undefined;

  const { onConfigUpsert, isLoading: isSaving } = useConfigUpsert(merchantId);
  const { onCallbackUpsert, isLoading: isSavingCallbacks } = useCallbackUpsert(merchantId);
  const [deleteCallback] = useDeleteCallbackMutation();

  return (
    <PageWrapper
      showParentHierarchyBanner
      title="Agentic Checkout"
      description="Manage the Agentic Checkout configuration for this merchant. These settings control transaction limits and API call restrictions for the Skipify MCP."
      isLoading={merchantInfoLoading || isLoadingConfigs || isLoadingCallbacks}
      data-testid="AGENTIC_CHECKOUT"
    >
      <AgenticCheckoutForm
        key={agenticCheckoutConfig?.updatedAt ?? merchantId}
        agenticCheckoutConfig={agenticCheckoutConfig}
        callbacks={callbacks}
        onSave={async ({ config, callback }) => {
          const callbackId = callbacks?.find(
            (cb) => cb.callbackType === 'AgenticCheckoutValidateTotal',
          )?.callbackId;
          const promises: Promise<unknown>[] = [];
          promises.push(
            onConfigUpsert({
              enabled: true,
              name: 'AGENTIC_CHECKOUT',
              value: config,
              configId: agenticCheckoutConfig?.configId,
              merchantId: agenticCheckoutConfig?.merchantId,
            }),
          );
          if (callback.isEnabled) {
            promises.push(
              onCallbackUpsert({
                callbackId,
                merchantId: agenticCheckoutConfig?.merchantId,
                callbackType: 'AgenticCheckoutValidateTotal',
                url: callback.validateTotalCallbackUrl ?? '',
              }),
            );
          } else if (callbackId) {
            promises.push(
              deleteCallback({
                callbackId,
                merchantId: agenticCheckoutConfig?.merchantId as string,
              }),
            );
          }
          try {
            await Promise.all(promises);
            toast.success('Changes saved successfully');
          } catch {
            toast.error(
              'An error occurred while saving these changes. Please try again later or contact Skipify Support',
            );
          }
        }}
        isSaving={isSaving || isSavingCallbacks}
        readOnly={!hasModifyAccess}
      />
    </PageWrapper>
  );
};
